import { isFinite, isNaN } from "lodash";
import { ModificationType } from "../Reference";
import ItemType from "../reference/ItemType";
import * as AccountingUtils from "../AccountingUtils";
import * as PSHelper from "../ProfessionalServiceHelper";
import RevenueArrangementItemProcessor from "./RevenueArrangementItemProcessor";
import RevenueArrangementUtils from "./RevenueArrangementUtils";
import { Utils, Comparator } from "revlock-webutils";
const { formatDate } = Utils;
import CreditNoteRevenueAllocationHelper from "./CreditNoteRevenueAllocationHelper";
import {
    CreditNoteStrategies,
    NonCreditNoteStrategies
} from "./RevenueAllocationStrategy";
import RecognitionRule from "../reference/RecognitionRule";
import ProspectiveModificationHelper from "./ProspectiveModificationHelper";
import {
    getStandAlonePrice,
    getCreditNoteLineItems,
    getNonCreditNoteLineItems,
    isCreditNoteLineItem,
    isStandaloneItem
} from "../StandalonePriceHelper";
import { Codes } from "../ErrorCode";

/**
 * Processor class to handle Revenue Arrangement processing for a Sales Order.
 * This class processes each Revenue Arrangement Item, applies prospective modifications,
 * calculates total ESP, and builds revenue plans.
 * @class RevenueArrangementProcessor
 * @constructor
 * @param {Object} salesOrder - The Sales Order object containing Revenue Arrangements.
 * @param {Object} revenueArrangement - The Revenue Arrangement to process.
 * @param {Object} context - The processing context containing configuration and state.
 * @method process - Main method to orchestrate the processing of the Revenue Arrangement.
 * @returns {void}
 * @throws {Error} Throws an error if processing fails.
 * @example
 * const processor = new RevenueArrangementProcessor(salesOrder, revenueArrangement, context);
 * processor.process();
 */
export default class RevenueArrangementProcessor {
    constructor(salesOrder, revenueArrangement, context) {
        this.salesOrder = salesOrder;
        this.revenueArrangement = revenueArrangement;
        this.context = context;
        this.arrangementContext = {
            foundSSP: true,
            foundNonZeroSSP: false
        };
    }

    process() {
        //1. Build arrangement context
        this.buildArrangementContext();

        //2. Sort revenue arrangement items
        this.sortRevenueArrangementItems();

        //3. Process each revenue arrangement item
        this.processItems();

        // 4. If no SSP found, skip further processing
        if (!this.arrangementContext.foundSSP) {
            return;
        }

        // 5. Apply prospective modification if applicable
        this.applyProspectiveModification();

        // 6. Populate delivery logs for professional services
        this.populateDeliveryLogs();

        // 7. Calculate total booking
        this.calculateTotalBooking();

        // 8. Calculate total ESP
        this.calculateTotalESP();

        // 9. Populate arrangement context with additional data
        this.populateArrangementContext();

        // 10. Calculate product revenue for each arrangement item
        this.calculateProductRevenue();

        // 11. Build revenue plan for each arrangement item
        this.buildRevenuePlan();

        // 12. Process termination credit notes if applicable
        this.processTerminationCreditNotes();
    }

    buildArrangementContext() {
        const { salesOrder, revenueArrangement } = this;

        // Create a map of salesOrderItemId -> salesOrderItem for easy lookup
        const soiById = {};
        salesOrder.salesOrderItem.forEach((soi) => {
            soiById[soi.rootId || soi.id] = soi;
        });
        this.arrangementContext.soiById = soiById;

        // Create a map of salesOrderItemId -> revenueArrangementItem for easy lookup
        const raItemsBySoId = {};
        revenueArrangement.revenueArrangementItem.forEach((rai) => {
            if (!ItemType.isFrozen(rai.itemType)) {
                raItemsBySoId[rai.salesOrderItemId] = rai;
            }
        });
        this.arrangementContext.raItemsBySoId = raItemsBySoId;

        let poCnParentItemIds = new Set();
        for (const item of revenueArrangement.revenueArrangementItem) {
            const { salesOrderItem, itemType } = item;
            if (
                salesOrderItem.parentSalesOrderItemId &&
                salesOrderItem.isCreditNote &&
                salesOrderItem.referenceLineItemId &&
                !ItemType.isFrozen(itemType)
            ) {
                poCnParentItemIds.add(salesOrderItem.parentSalesOrderItemId);
            }
        }
        this.arrangementContext.poCnParentItemIds = poCnParentItemIds;
    }

    sortRevenueArrangementItems() {
        // sort so that parent items are processed first.
        this.revenueArrangement.revenueArrangementItem.sort((a, b) => {
            const aParentId = a.salesOrderItem.parentSalesOrderItemId;
            const bParentId = b.salesOrderItem.parentSalesOrderItemId;
            if (!aParentId && bParentId) return -1;
            if (aParentId && !bParentId) return 1;
            return 0;
        });
    }

    processItems() {
        // Process each revenue arrangement item
        this.revenueArrangement.revenueArrangementItem.forEach((item) => {
            const processor = new RevenueArrangementItemProcessor(
                this.salesOrder,
                this.revenueArrangement,
                item,
                this.context,
                this.arrangementContext
            );
            processor.process();
        });
    }

    applyProspectiveModification() {
        const { salesOrder, revenueArrangement, context, arrangementContext } =
            this;
        const {
            orgConfig,
            orgConfigByKey,
            calendarConfig,
            removedRevenueArrangement
        } = context;

        const previousRevenueArrangement =
            removedRevenueArrangement || salesOrder.currentRevenueArrangement;
        if (
            revenueArrangement.modificationType ==
                ModificationType.PROSPECTIVE &&
            salesOrder.newRevenueArrangement &&
            salesOrder.newRevenueArrangement.id == revenueArrangement.id &&
            previousRevenueArrangement
        ) {
            ProspectiveModificationHelper.applyProspectiveModification(
                salesOrder,
                revenueArrangement,
                previousRevenueArrangement,
                orgConfig,
                orgConfigByKey,
                calendarConfig
            );
        }
    }

    populateDeliveryLogs() {
        const { salesOrder, revenueArrangement, context } = this;
        const { orgConfig, orgConfigByKey, errors, psdByProductCode } = context;

        const { id: activeRaId } =
            salesOrder.newRevenueArrangement ||
            salesOrder.currentRevenueArrangement;

        const willPopulate = !!(
            psdByProductCode && revenueArrangement.id == activeRaId
        );

        if (willPopulate) {
            PSHelper.populateDeliveryLogs(
                revenueArrangement,
                salesOrder,
                orgConfig,
                errors
            );
        }
    }

    calculateTotalBooking() {
        const { revenueArrangement, context } = this;
        const { orgConfig, expenseReasonCode } = context;
        const { poCnParentItemIds } = this.arrangementContext;

        let totalESP = 0;
        let standaloneESP = 0;

        let sspType = (item) =>
            item.ssp.standalonePrice &&
            typeof item.ssp.standalonePrice === "object" &&
            item.ssp.standalonePrice.type == "RESIDUAL"
                ? "RESIDUAL"
                : "NON_RESIDUAL";

        revenueArrangement.revenueArrangementItem.sort(
            Comparator.getComparator(
                [sspType],
                [Comparator.forType("string")],
                false
            )
        );

        let totalBooking = 0;
        let totalCnBooking = 0;
        let totalPoCnBooking = 0;
        let useLocalTotalBooking = false;
        let filteredRaItems;
        if (expenseReasonCode && expenseReasonCode.length) {
            // we have reason code enabled, we need to re-calulcate the totalBooking by excluding the items which have reason codes.
            filteredRaItems = revenueArrangement.revenueArrangementItem.filter(
                (item) => !AccountingUtils.itemHasReasonCode(item, orgConfig)
            );
            useLocalTotalBooking = filteredRaItems.length > 0;
        }
        if (
            revenueArrangement.modificationType == ModificationType.PROSPECTIVE
        ) {
            // salesorder has prospective modification, exclude frozen items from calculating totalBooking
            filteredRaItems =
                filteredRaItems || revenueArrangement.revenueArrangementItem;
            useLocalTotalBooking = true;
            filteredRaItems = filteredRaItems.filter(
                (item) => !ItemType.isFrozen(item.itemType)
            );
        }

        if (useLocalTotalBooking) {
            filteredRaItems.forEach((item) => {
                const { itemType, unrecognizedRevenue, salesOrderItem } = item;
                if (
                    itemType == ItemType.SPLIT_UNRECOGNIZED ||
                    (itemType == ItemType.UNCHANGED &&
                        unrecognizedRevenue != null)
                ) {
                    totalBooking += unrecognizedRevenue;
                } else {
                    totalBooking += salesOrderItem.extendedSalePrice;
                }
            });
        }

        revenueArrangement.revenueArrangementItem.forEach((item) => {
            const { salesOrderItem } = item;
            if (
                poCnParentItemIds.has(
                    salesOrderItem.rootId || salesOrderItem.id
                )
            ) {
                totalPoCnBooking += salesOrderItem.extendedSalePrice;
            }

            if (
                isCreditNoteLineItem(item, orgConfig) &&
                !isStandaloneItem(item)
            ) {
                totalCnBooking += salesOrderItem.extendedSalePrice;
            }
        });

        this.arrangementContext.totalBooking = totalBooking;
        this.arrangementContext.totalCnBooking = totalCnBooking;
        this.arrangementContext.totalPoCnBooking = totalPoCnBooking;
        this.arrangementContext.useLocalTotalBooking = useLocalTotalBooking;
    }

    calculateTotalESP() {
        const { salesOrder, revenueArrangement, context, arrangementContext } =
            this;
        const { orgConfig, roundPrecision, errors } = context;
        let {
            totalBooking,
            useLocalTotalBooking = false,
            poCnParentItemIds = new Set(),
            totalESP = 0,
            standaloneESP = 0,
            foundSSP,
            foundNonZeroSSP,
            totalPoCnBooking = 0,
            totalCnBooking = 0
        } = arrangementContext;

        revenueArrangement.revenueArrangementItem.forEach((item) => {
            let { salesOrderItem } = item;

            if (Utils.isEmpty(item.estimatedSalePrice)) {
                item.estimatedSalePrice = getStandAlonePrice(
                    item,
                    revenueArrangement,
                    salesOrder,
                    orgConfig
                );
            }

            if (
                Utils.isNumber(item.estimatedSalePrice) &&
                item.estimatedSalePrice != 0 &&
                item.ssp.standalonePrice &&
                item.ssp.standalonePrice.type !== "STANDALONE"
            ) {
                foundNonZeroSSP = true;
            }

            if (Utils.isEmpty(item.totalBooking)) {
                item.totalBooking = salesOrder.totalBooking;
            }

            if (useLocalTotalBooking) {
                // we can't use sales order total booking since the above variable
                // has incorporated reason code expense as well, which we can't use.
                item.totalBooking = totalBooking;
            }

            item.actualSalePrice = salesOrderItem.actualSalePrice;
            item.extendedSalePrice = salesOrderItem.extendedSalePrice;

            item.extendedEstimatedSalePrice =
                item.estimatedSalePrice * salesOrderItem.quantity;

            // do not include frozen items to calculate totalESP
            if (ItemType.isFrozen(item.itemType)) {
                return;
            }

            if (item.itemType == ItemType.SPLIT_UNRECOGNIZED) {
                if (
                    item.ssp.standalonePrice?.type == "AS_IS" ||
                    item.ssp.standalonePrice.type == "STANDALONE"
                ) {
                    item.extendedEstimatedSalePrice = item.unrecognizedRevenue;
                } else {
                    item.extendedEstimatedSalePrice =
                        item.estimatedSalePrice * item.unrecognizedQuantity;
                }
            }

            if (
                item.ssp.standalonePrice &&
                item.ssp.standalonePrice.type == "STANDALONE"
            ) {
                // We separated STANDALONE esp thinking about the significant financing component.
                // That should not have been handled with STANDALONE.
                standaloneESP += item.extendedEstimatedSalePrice;
            } else if (
                (!salesOrderItem.referenceLineItemId ||
                    (salesOrderItem.referenceLineItemId &&
                        item.ssp.standalonePrice &&
                        item.ssp.standalonePrice.type == "AS_IS")) &&
                !AccountingUtils.itemHasReasonCode(item, orgConfig)
            ) {
                // if this item has reason code, we can't include this in the totalESP for calc [REV-8406]
                totalESP += item.extendedEstimatedSalePrice;
            }

            if (
                !item.recognitionRuleId ==
                    RecognitionRule.PERCENT_OF_COMPLETE &&
                Utils.isEmpty(salesOrderItem.estimatedTotalCost)
            ) {
                errors.add(Codes.TRAN_11, {
                    salesOrder,
                    salesOrderItem,
                    revenueArrangementItem: item
                });
                foundSSP = false;
            }
        });

        if (isNaN(totalESP) || !isFinite(totalESP)) {
            totalESP = 0;
        }

        totalESP = AccountingUtils.toFixedNum(totalESP, roundPrecision);

        this.arrangementContext.totalESP = totalESP;
        this.arrangementContext.foundSSP = foundSSP;
        this.arrangementContext.foundNonZeroSSP = foundNonZeroSSP;
        this.arrangementContext.standaloneESP = standaloneESP;
    }

    populateArrangementContext() {
        const { revenueArrangement, context } = this;
        const { orgConfig, recognizeByTransactionDate } = context;

        // REV-10421 any sales order item that has a referenceLineItemId, it means it is a credit note line item.
        // we want to process non credit note line items first and then process credit note lines
        // since credit note lines depend on ssp value (estimatedSalePrice) of non credit note line item

        // NOTE: if we have reason code, and that item has reason code, bad debt expense work will happen on this
        // we are not chaning this right now.
        const creditNoteRevenueArrangementItem = getCreditNoteLineItems(
            revenueArrangement.revenueArrangementItem,
            orgConfig
        );

        // all non credit note lines. The ones that have reason codes, they will be returned even though they are credit notes.
        const nonCreditNoteRevenueArrangementItem = getNonCreditNoteLineItems(
            revenueArrangement.revenueArrangementItem,
            orgConfig
        );

        this.arrangementContext.creditNoteRevenueArrangementItem =
            creditNoteRevenueArrangementItem;
        this.arrangementContext.nonCreditNoteRevenueArrangementItem =
            nonCreditNoteRevenueArrangementItem;

        const raiBySalesOrderItemId = {};
        const poTotalESPByParentSalesOrderItemId = {};
        // maintain totalESP of po items by their parentSalesOrderItemId
        revenueArrangement.revenueArrangementItem.forEach((rai) => {
            // for a prospective modified order, do not include the split frozen item
            if (rai.itemType == ItemType.SPLIT_FROZEN) return;
            raiBySalesOrderItemId[
                rai.salesOrderItem.rootId || rai.salesOrderItem.id
            ] = rai;
            const parentSalesOrderItemId =
                rai.salesOrderItem.parentSalesOrderItemId;
            if (parentSalesOrderItemId) {
                let totalESP =
                    poTotalESPByParentSalesOrderItemId[
                        parentSalesOrderItemId
                    ] || 0;
                totalESP += rai.extendedEstimatedSalePrice;
                poTotalESPByParentSalesOrderItemId[parentSalesOrderItemId] =
                    totalESP;
            }
        });
        this.arrangementContext.raiBySalesOrderItemId = raiBySalesOrderItemId;
        this.arrangementContext.poTotalESPByParentSalesOrderItemId =
            poTotalESPByParentSalesOrderItemId;

        const nonCreditNoteRAIByReferenceLineItemId = {};
        const creditNoteRAIByReferenceLineItemId = {};
        const nonCreditNoteRAIByProductId = {};
        const creditNoteRAIByProductId = {};
        // maintaing credit note and non credit note line item rai by item id for which credit note came.
        if (creditNoteRevenueArrangementItem.length) {
            creditNoteRevenueArrangementItem.forEach((rai) => {
                if (
                    !creditNoteRAIByReferenceLineItemId[
                        rai.salesOrderItem.referenceLineItemId
                    ]
                ) {
                    creditNoteRAIByReferenceLineItemId[
                        rai.salesOrderItem.referenceLineItemId
                    ] = [];
                }
                creditNoteRAIByReferenceLineItemId[
                    rai.salesOrderItem.referenceLineItemId
                ].push(rai);

                if (!creditNoteRAIByProductId[rai.salesOrderItem.productId]) {
                    creditNoteRAIByProductId[rai.salesOrderItem.productId] = [];
                }
                creditNoteRAIByProductId[rai.salesOrderItem.productId].push(
                    rai
                );
            });

            nonCreditNoteRevenueArrangementItem.forEach((rai) => {
                nonCreditNoteRAIByReferenceLineItemId[
                    rai.salesOrderItem.rootId || rai.salesOrderItem.id
                ] = rai;
                if (
                    !nonCreditNoteRAIByProductId[rai.salesOrderItem.productId]
                ) {
                    nonCreditNoteRAIByProductId[rai.salesOrderItem.productId] =
                        [];
                }
                nonCreditNoteRAIByProductId[rai.salesOrderItem.productId].push(
                    rai
                );
            });
        }

        this.arrangementContext.nonCreditNoteRAIByReferenceLineItemId =
            nonCreditNoteRAIByReferenceLineItemId;
        this.arrangementContext.creditNoteRAIByReferenceLineItemId =
            creditNoteRAIByReferenceLineItemId;
        this.arrangementContext.nonCreditNoteRAIByProductId =
            nonCreditNoteRAIByProductId;
        this.arrangementContext.creditNoteRAIByProductId =
            creditNoteRAIByProductId;

        let totalBookingByTransactionDate, totalEspByTransactionDate;
        // Find No of Discount items in the revenue arrangement item.

        const items = revenueArrangement.revenueArrangementItem;
        let noOfDiscountItems = items.filter((item) =>
            AccountingUtils.isDiscountProduct(item.salesOrderItem.product)
        ).length;
        // This is a case where we will get a [Discount for invoice] and [Discount reversal for a creditNote]. (check transactionDate_multiple_discounts test case)
        if (recognizeByTransactionDate && noOfDiscountItems > 1) {
            totalBookingByTransactionDate = Utils.sumByField(
                items,
                "salesOrderItem.transactionDate",
                "extendedSalePrice"
            );
            totalEspByTransactionDate = Utils.sumByField(
                items,
                "salesOrderItem.transactionDate",
                "extendedEstimatedSalePrice"
            );
        }
        this.arrangementContext.totalBookingByTransactionDate =
            totalBookingByTransactionDate;
        this.arrangementContext.totalEspByTransactionDate =
            totalEspByTransactionDate;
        this.arrangementContext.noOfDiscountItems = noOfDiscountItems;
    }

    calculateProductRevenue() {
        const { revenueArrangement, context, arrangementContext, salesOrder } =
            this;
        const {
            creditNoteRevenueArrangementItem,
            nonCreditNoteRevenueArrangementItem
        } = arrangementContext;

        normRevenueArrangementItem(revenueArrangement);

        creditNoteRevenueArrangementItem.forEach((item) => {
            const params = {
                salesOrder,
                revenueArrangement,
                revenueArrangementItem: item,
                context,
                arrangementContext
            };
            const strategies = CreditNoteStrategies.getInstance();
            const strategy = strategies.selectStrategy(params);
            strategy.calculate(params);
        });

        nonCreditNoteRevenueArrangementItem.forEach((item) => {
            const params = {
                salesOrder,
                revenueArrangement,
                revenueArrangementItem: item,
                context,
                arrangementContext
            };
            const strategies = NonCreditNoteStrategies.getInstance();
            const strategy = strategies.selectStrategy(params);
            strategy.calculate(params);
        });
    }

    buildRevenuePlan() {
        const { salesOrder, revenueArrangement, context, arrangementContext } =
            this;
        const {
            orgConfig,
            orgConfigByKey,
            calendarConfig,
            cnAllocationByBacklog,
            unearnedByValue,
            options,
            roundPrecision,
            customCode,
            errors
        } = context;

        const {
            creditNoteRevenueArrangementItem,
            nonCreditNoteRevenueArrangementItem
        } = arrangementContext;

        let revenueArrangementItems = revenueArrangement.revenueArrangementItem;
        if (revenueArrangement.isTerminated && cnAllocationByBacklog) {
            revenueArrangementItems = nonCreditNoteRevenueArrangementItem;
        }

        for (const item of revenueArrangementItems) {
            if (isNaN(item.productRevenue) || !isFinite(item.productRevenue)) {
                item.productRevenue = 0;
            }

            // skip frozen items
            if (ItemType.isFrozen(item.itemType)) {
                continue;
            }

            item.productRevenue = AccountingUtils.toFixedNum(
                item.productRevenue,
                roundPrecision
            );

            item.relativeSalePricePercent =
                item.productRevenue / item.totalBooking;
            if (!Utils.isNumber(item.relativeSalePricePercent)) {
                item.relativeSalePricePercent = 0;
            }

            if (
                options.createRevenuePlan &&
                ((revenueArrangement.state == "New" &&
                    item.recognitionRule &&
                    !item.hasManualUpdates) ||
                    !item.plan ||
                    item.plan.length == 0)
            ) {
                try {
                    RevenueArrangementUtils.buildDefaultRevenuePlan(
                        item,
                        salesOrder,
                        orgConfig,
                        customCode,
                        errors
                    );
                } catch (e) {
                    throw e;
                }
            }

            if (
                item.plan &&
                (item.recognitionRule.id ===
                    RecognitionRule.PROPORTIONAL_PERFORMANCE ||
                    item.recognitionRule.id ===
                        RecognitionRule.PERCENT_OF_COMPLETE)
            ) {
                RevenueArrangementUtils.buildPSPlan(
                    item,
                    salesOrder,
                    revenueArrangement,
                    errors,
                    calendarConfig,
                    orgConfig,
                    orgConfigByKey
                );
            }

            if (item.plan && unearnedByValue !== "customer") {
                RevenueArrangementUtils.addBillingPeriodsToPlan(
                    item.plan,
                    salesOrder,
                    calendarConfig
                );
            }
        }
    }

    processTerminationCreditNotes() {
        const { salesOrder, revenueArrangement, context, arrangementContext } =
            this;
        const {
            orgConfigByKey,
            options,
            errors,
            clientReferenceData,
            cnAllocationByBacklog
        } = context;
        const {
            creditNoteRevenueArrangementItem,
            nonCreditNoteRevenueArrangementItem
        } = arrangementContext;

        if (revenueArrangement.isTerminated && cnAllocationByBacklog) {
            CreditNoteRevenueAllocationHelper.processTerminationCreditNotes(
                salesOrder,
                revenueArrangement,
                creditNoteRevenueArrangementItem,
                nonCreditNoteRevenueArrangementItem,
                clientReferenceData,
                orgConfigByKey,
                options,
                errors
            );
        }
    }
}

function normRevenueArrangementItem(revenueArrangement) {
    revenueArrangement.revenueArrangementItem.forEach((item) => {
        item.deliveryStartDate =
            item.deliveryStartDate && formatDate(item.deliveryStartDate);
        item.deliveryEndDate =
            item.deliveryEndDate && formatDate(item.deliveryEndDate);

        item.goLiveDate = item.goLiveDate && formatDate(item.goLiveDate);

        if (!item.deliveryStartDate && item.goLiveDate) {
            item.deliveryStartDate = item.goLiveDate;
        } else if (item.deliveryStartDate && !item.goLiveDate) {
            item.goLiveDate = item.deliveryStartDate;
        }

        if (!item.recognitionRule && item.ssp && item.ssp.recognitionRuleId) {
            const ruleKey = Object.keys(RecognitionRule).find((ruleKey) => {
                const value = RecognitionRule[ruleKey];
                if (value == item.ssp.recognitionRuleId) {
                    return true;
                }
            });

            item.recognitionRule = {
                id: item.ssp.recognitionRuleId,
                name: ruleKey
            };

            item.recognitionRuleId = item.ssp.recognitionRuleId;
        }
    });
}
